import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import fontsData from 'src/assets/fonts.json';
import usePrevious from 'src/js/modules/utils/custom-hooks/use-previous';
import FontPreviewOption from './font-preview-option';
import ValueInputControl from './controls/value-input-control';
import SearchableSelectControl from './controls/searchable-select-control';
import ActionRadioGroupControl from './controls/action-radio-group-control';
import AutocompleteControl from './controls/autocomplete-control';
import {
  fontWeightVariantsMap,
  capitalizationOptions,
  elementStylesMap,
  letterSpacingSettings,
} from '../constants/element-customization';
import constants from '../../common/constants';
import getDefaultFontSizeOptions from '../../extensions/registry/ws-text-formatting/helpers/get-default-font-size-options';
import getDefaultLineHeightOptions from '../helpers/get-default-line-height-options';

const MIN_FONT_SIZE = 1;
const MAX_FONT_SIZE = 400;
const MIN_LINE_HEIGHT = 0.06;
const MAX_LINE_HEIGHT = 100;

const {
  common: {
    ENTER_KEYCODE,
    ARROW_UP_KEYCODE,
    ARROW_DOWN_KEYCODE,
    ARROW_LEFT_KEYCODE,
    ARROW_RIGHT_KEYCODE,
    BACKSPACE_KEYCODE,
    DELETE_KEYCODE,
    TAB_KEYCODE,
  },
} = constants;

const ALLOWED_KEYCODES = [
  ENTER_KEYCODE,
  ARROW_UP_KEYCODE,
  ARROW_DOWN_KEYCODE,
  ARROW_LEFT_KEYCODE,
  ARROW_RIGHT_KEYCODE,
  BACKSPACE_KEYCODE,
  DELETE_KEYCODE,
  TAB_KEYCODE,
];

const {
  Box,
  Stack,
  Heading,
  Divider,
  HorizontalStack,
  ActionButton,
  NotificationMessage,
  Paragraph,
} = designSystem;

const { useWatch, useController } = reactHookForm;

const prepareFontsOptions = () => fontsData.map(({ family }) => ({ value: family, label: family }));

const getFontWeightOptions = (currentFont) => {
  const { variants } = fontsData.find(({ family }) => family === currentFont) || {};

  if (!variants) {
    return [{ value: '400', label: i18next.t('Regular') }];
  }

  const posibleVariants = Object.keys(fontWeightVariantsMap);
  const options = [];

  variants.forEach((variant) => {
    if (posibleVariants.includes(variant)) {
      options.push({
        value: variant,
        label: i18next.t('{fontWeighLabel}', { fontWeighLabel: fontWeightVariantsMap[variant] }),
      });
    }
  });

  return options;
};

const getExpandedOptions = (options, newValue) =>
  [...options, { value: newValue, label: newValue }].sort(
    ({ label: labelA }, { label: labelB }) => parseFloat(labelA) - parseFloat(labelB)
  );

const normalizeValue = (inputValue, currentValue, min, max) => {
  const newValue = Number(inputValue) || currentValue;
  return Math.round(Math.min(Math.max(newValue, min), max) * 100) / 100;
};

const getOption = (options, searcedValue) =>
  options.find(
    ({ value, defaultValue }) => value === searcedValue || defaultValue === searcedValue
  );

const processLineHeightValue = (value, basicLineHeightValues, variable) =>
  value === 'Auto' ? basicLineHeightValues[variable] : value;

const FontOption = ({ children, observer }) => (
  <FontPreviewOption observer={observer}>{children}</FontPreviewOption>
);

const ElementTypeSettings = ({
  control,
  id,
  captions,
  fontObserver,
  modalContentRef,
  contentRef,
  basicLineHeightValues,
  isAdvancedFontCustomizationAvailable,
  handlers,
  onManageColorsClick,
  handleFeatureAvailability,
}) => {
  const {
    handleFontFamilyChange,
    handleFontWeightChange,
    handleFontSizeChange,
    handleLineHeightChange,
    handleTextTransformChange,
    handleLetterSpacingChange,
  } = handlers;

  const fontsOptions = prepareFontsOptions();
  const defaultFontSizeOptions = getDefaultFontSizeOptions();
  const defaultLineHeightOptions = getDefaultLineHeightOptions(
    `${id}${elementStylesMap.LINE_HEIGHT}`,
    basicLineHeightValues
  );

  const { field: lineHeightField } = useController({
    control,
    name: `${id}${elementStylesMap.LINE_HEIGHT}`,
  });
  const { field: fontSizeField } = useController({
    control,
    name: `${id}${elementStylesMap.FONT_SIZE}`,
  });
  const [currentFont, currentWeight, currentSize, currentHeight, currentCapitalization] = useWatch({
    control,
    name: [
      `${id}${elementStylesMap.FONT_FAMILY}`,
      `${id}${elementStylesMap.FONT_WEIGHT}`,
      `${id}${elementStylesMap.FONT_SIZE}`,
      `${id}${elementStylesMap.LINE_HEIGHT}`,
      `${id}${elementStylesMap.TEXT_TRANSFORM}`,
    ],
  });
  const fontWeightOptions = useMemo(() => getFontWeightOptions(currentFont), [currentFont]);
  const { id: selectedCapitalizationId } = capitalizationOptions.find(
    (option) => option.value === currentCapitalization
  );

  const [sizeInputValue, setSizeInputValue] = useState(currentSize);
  const [heightInputValue, setHeightInputValue] = useState(
    processLineHeightValue(
      currentHeight,
      basicLineHeightValues,
      `${id}${elementStylesMap.LINE_HEIGHT}`
    )
  );
  const [fontSizeOptions, setFontSizeOptions] = useState(defaultFontSizeOptions);
  const [lineHeightOptions, setLineHeightOptions] = useState(defaultLineHeightOptions);

  const fontSizeRef = useRef(null);
  const lineHeightRef = useRef(null);
  const fontSizeLastKeyPressed = useRef(null);
  const lineHeightLastKeyPressed = useRef(null);

  const prevFontSize = usePrevious(currentSize);
  const prevLineHeight = usePrevious(currentHeight);

  const handleFontSizeInputChange = (value) => {
    setSizeInputValue(value);
  };

  const handleLineHeightInputChange = (value) => {
    const normalizedValue = value.replace(/[^0-9.]+/g, '').replace(/(\..*)\./g, '$1');
    setHeightInputValue(normalizedValue);
  };

  const onFontSizeSelect = (option) => {
    if (!option) {
      return;
    }

    handleFontSizeChange(`${id}${elementStylesMap.FONT_SIZE}`, option.value);
    setSizeInputValue(option.value);
  };

  const onLineHeightSelect = (option) => {
    if (!option) {
      return;
    }

    const newValue = option.value === 'Auto' ? option.defaultValue : option.value;

    handleLineHeightChange(`${id}${elementStylesMap.LINE_HEIGHT}`, newValue);
    setHeightInputValue(newValue);
  };

  const handleFontSizeKeyDown = (event, { focusedOption }) => {
    if (!/^\d*$/.test(event.key) && !ALLOWED_KEYCODES.includes(event.keyCode)) {
      event.preventDefault();
      return;
    }

    if (event.keyCode !== ENTER_KEYCODE) {
      fontSizeLastKeyPressed.current = event.keyCode;
      return;
    }

    event.preventDefault();

    if (
      fontSizeLastKeyPressed.current === ARROW_UP_KEYCODE ||
      fontSizeLastKeyPressed.current === ARROW_DOWN_KEYCODE
    ) {
      onFontSizeSelect(focusedOption);
      fontSizeField.onChange(focusedOption.value);
      return;
    }

    if (!sizeInputValue) {
      return;
    }

    const normalizedValue = String(
      normalizeValue(sizeInputValue, currentSize, MIN_FONT_SIZE, MAX_FONT_SIZE)
    );
    fontSizeField.onChange(normalizedValue);
  };

  const handleLineHeightKeyDown = (event, { focusedOption }) => {
    if (!/^\d*\.?\d*$/.test(event.key) && !ALLOWED_KEYCODES.includes(event.keyCode)) {
      event.preventDefault();
      return;
    }

    if (event.keyCode !== ENTER_KEYCODE) {
      lineHeightLastKeyPressed.current = event.keyCode;
      return;
    }

    event.preventDefault();

    if (
      lineHeightLastKeyPressed.current === ARROW_UP_KEYCODE ||
      lineHeightLastKeyPressed.current === ARROW_DOWN_KEYCODE
    ) {
      onLineHeightSelect(focusedOption);
      const newValue =
        focusedOption.value === 'Auto' ? focusedOption.defaultValue : focusedOption.value;
      lineHeightField.onChange(newValue);
      return;
    }

    if (!heightInputValue) {
      return;
    }

    const normalizedValue = String(
      normalizeValue(heightInputValue, currentHeight, MIN_LINE_HEIGHT, MAX_LINE_HEIGHT)
    );

    if (getOption(defaultLineHeightOptions, `${normalizedValue}.0`)) {
      lineHeightField.onChange(`${normalizedValue}.0`);
      return;
    }

    lineHeightField.onChange(normalizedValue);
  };

  useEffect(() => {
    if (!currentSize) {
      fontSizeField.onChange(prevFontSize);
      return;
    }

    let optionToSelect = getOption(defaultFontSizeOptions, currentSize);

    if (!optionToSelect) {
      const normalizedValue = String(
        normalizeValue(sizeInputValue, currentSize, MIN_FONT_SIZE, MAX_FONT_SIZE)
      );
      const expandedOptions = getExpandedOptions(defaultFontSizeOptions, normalizedValue);
      setFontSizeOptions(expandedOptions);
      optionToSelect = getOption(expandedOptions, normalizedValue);
      fontSizeRef?.current?.selectOption(optionToSelect);

      return;
    }

    fontSizeRef?.current?.selectOption(optionToSelect);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [currentSize]);

  useEffect(() => {
    if (!currentHeight) {
      lineHeightField.onChange(
        processLineHeightValue(
          prevLineHeight,
          basicLineHeightValues,
          `${id}${elementStylesMap.LINE_HEIGHT}`
        )
      );
      return;
    }

    let optionToSelect =
      getOption(defaultLineHeightOptions, currentHeight) ||
      getOption(defaultLineHeightOptions, `${currentHeight}.0`);

    if (!optionToSelect) {
      const normalizedValue = String(
        normalizeValue(heightInputValue, currentHeight, MIN_LINE_HEIGHT, MAX_LINE_HEIGHT)
      );
      const expandedOptions = getExpandedOptions(defaultLineHeightOptions, normalizedValue);
      setLineHeightOptions(expandedOptions);
      optionToSelect = getOption(expandedOptions, normalizedValue);
      lineHeightRef?.current?.selectOption(optionToSelect);

      return;
    }

    lineHeightRef?.current?.selectOption(optionToSelect);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [currentHeight]);

  return (
    <>
      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-s"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <Stack gap="spacing-2-xs">
          <SearchableSelectControl
            title={captions.settings.font}
            control={control}
            name={`${id}${elementStylesMap.FONT_FAMILY}`}
            options={fontsOptions}
            components={{
              Option: (props) => <FontOption {...props} observer={fontObserver} />,
            }}
            noResultsText={captions.noResultsText}
            maxMenuHeight={205}
            modalContentRef={modalContentRef}
            contentRef={contentRef}
            isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
            onChange={(value) => handleFontFamilyChange(id, value)}
            onFocus={() => handleFeatureAvailability(id, elementStylesMap.FONT_FAMILY)}
          />
          <SearchableSelectControl
            key={currentWeight}
            title={captions.settings.weight}
            control={control}
            name={`${id}${elementStylesMap.FONT_WEIGHT}`}
            options={fontWeightOptions}
            noResultsText={captions.noResultsText}
            modalContentRef={modalContentRef}
            contentRef={contentRef}
            isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
            onChange={(value) =>
              handleFontWeightChange(`${id}${elementStylesMap.FONT_WEIGHT}`, value)
            }
            onFocus={() => handleFeatureAvailability(id, elementStylesMap.FONT_WEIGHT)}
          />
          <HorizontalStack gap="spacing-m" verticalAlign="center">
            <AutocompleteControl
              title={captions.settings.size}
              short
              ref={fontSizeRef}
              control={control}
              name={`${id}${elementStylesMap.FONT_SIZE}`}
              options={fontSizeOptions}
              modalContentRef={modalContentRef}
              contentRef={contentRef}
              isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
              onChange={onFontSizeSelect}
              onFocus={() => handleFeatureAvailability(id, elementStylesMap.FONT_SIZE)}
              inputValue={sizeInputValue}
              onInputChange={handleFontSizeInputChange}
              onKeyDown={handleFontSizeKeyDown}
            />
            <AutocompleteControl
              title={captions.settings.height}
              short
              ref={lineHeightRef}
              control={control}
              name={`${id}${elementStylesMap.LINE_HEIGHT}`}
              options={lineHeightOptions}
              maxMenuHeight={180}
              modalContentRef={modalContentRef}
              contentRef={contentRef}
              isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
              onChange={onLineHeightSelect}
              onFocus={() => handleFeatureAvailability(id, elementStylesMap.LINE_HEIGHT)}
              inputValue={heightInputValue}
              onInputChange={handleLineHeightInputChange}
              onKeyDown={handleLineHeightKeyDown}
            />
          </HorizontalStack>
          {Number(currentSize) > 64 && (
            <NotificationMessage
              appearance="info"
              iconGlyph="info"
              title={<Paragraph>{captions.fontSizeNotification}</Paragraph>}
            />
          )}
        </Stack>
      </Box>

      <Divider appearance="light-grey" />

      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <HorizontalStack align="space-between" verticalAlign="center">
          <Heading type="heading-6">{captions.settings.color}</Heading>
          <ActionButton
            label={captions.manageColorsButton}
            format="text"
            iconGlyph="small-right-arrow"
            iconPlacement="after"
            onClick={() => onManageColorsClick(id)}
          />
        </HorizontalStack>
      </Box>

      <Divider appearance="light-grey" />

      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <ActionRadioGroupControl
          title={captions.settings.capitalization}
          options={capitalizationOptions}
          selectedId={selectedCapitalizationId}
          isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
          onChange={(option) =>
            handleTextTransformChange(id, `${id}${elementStylesMap.TEXT_TRANSFORM}`, option.value)
          }
        />
      </Box>

      <Divider appearance="light-grey" />

      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <ValueInputControl
          title={captions.settings.letterSpacing}
          control={control}
          name={`${id}${elementStylesMap.LETTER_SPACING}`}
          onChange={(value) =>
            handleLetterSpacingChange(`${id}${elementStylesMap.LETTER_SPACING}`, value)
          }
          onFocus={() => handleFeatureAvailability(id, elementStylesMap.LETTER_SPACING)}
          {...letterSpacingSettings}
        />
      </Box>
    </>
  );
};

FontOption.propTypes = {
  children: PropTypes.node.isRequired,
  observer: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
};

ElementTypeSettings.propTypes = {
  control: PropTypes.shape({}).isRequired,
  id: PropTypes.string.isRequired,
  captions: PropTypes.shape({
    settings: PropTypes.shape({
      font: PropTypes.string,
      weight: PropTypes.string,
      size: PropTypes.string,
      height: PropTypes.string,
      color: PropTypes.string,
      capitalization: PropTypes.string,
      letterSpacing: PropTypes.string,
    }),
    manageColorsButton: PropTypes.string,
    fontSizeNotification: PropTypes.string,
    noResultsText: PropTypes.string,
  }).isRequired,
  fontObserver: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  modalContentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  contentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  basicLineHeightValues: PropTypes.shape({}).isRequired,
  isAdvancedFontCustomizationAvailable: PropTypes.bool.isRequired,
  handlers: PropTypes.shape({
    handleFontFamilyChange: PropTypes.func.isRequired,
    handleFontWeightChange: PropTypes.func.isRequired,
    handleFontSizeChange: PropTypes.func.isRequired,
    handleLineHeightChange: PropTypes.func.isRequired,
    handleTextTransformChange: PropTypes.func.isRequired,
    handleLetterSpacingChange: PropTypes.func.isRequired,
  }).isRequired,
  onManageColorsClick: PropTypes.func.isRequired,
  handleFeatureAvailability: PropTypes.func.isRequired,
};

export default ElementTypeSettings;
